import { UniqueIdentifier } from "@dnd-kit/core";
import { ReturnsAndClaimsPermissions } from "@redotech/account-common/permissions";
import { SortableTree } from "@redotech/arbiter/navigation/folder-tree/sortable-tree";
import {
  TreeItem,
  TreeItemType,
  TreeViewItem,
} from "@redotech/arbiter/navigation/folder-tree/types";
import { flattenTree } from "@redotech/arbiter/navigation/folder-tree/utilities";
import { RedoSecondaryNavItem } from "@redotech/arbiter/navigation/secondary/redo-secondary-nav-item";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useCapability } from "@redotech/redo-merchant-app-common/use-capability";
import { useGiftReceiptRequestsCount } from "@redotech/redo-merchant-general-app/gift-receipt-requests/use-gift-receipt-requests-count";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import {
  NEEDS_ATTENTION_FOLDER_NAME,
  ReturnSystemView,
} from "@redotech/redo-return-operations-common/views/default-return-views";
import {
  default as RETURNS_ICON,
  default as RETURNS_ICON_FILLED,
} from "@redotech/redo-web/icon-old/flip-backward.svg";
import { memo, useMemo } from "react";
import { useMatch } from "react-router-dom";
import { recordListHref } from "../../../../return/record-links/record-links";
import { ReturnViewOrderingContext } from "../../../../services/returns/returns-view-ordering-service";
import { nameReturnsNavTree } from "../returns/returns-nav-action-names";
import { useViewFolderLogic } from "./use-view-folder-logic";
import {
  treeToViewsAndFolders,
  viewsAndFoldersToTree,
} from "./view-to-tree-utils";

export const ReturnsItem = memo(function ReturnsItem() {
  const { t, i18n } = useTranslation();
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const returnsEnabled = useCapability("returns");
  const urlPrefix = routes.returnsHref;
  const returnsInstantExchangeEnabled = team.settings.exchanges.allowInstant;

  const hasPermissionToViewItem = useHasPermission(
    ReturnsAndClaimsPermissions.VIEW_RETURN,
  );

  const returnViewOrderingContext = useRequiredContext(
    ReturnViewOrderingContext,
  );
  const { notificationCounts } = returnViewOrderingContext;

  const allViews = useMemo(() => {
    return [...returnViewOrderingContext.views].filter(
      (view) => view.name !== ReturnSystemView.CREATE,
    );
  }, [returnViewOrderingContext]);

  const allFolders = useMemo(() => {
    return [...returnViewOrderingContext.folders];
  }, [returnViewOrderingContext]);

  const {
    viewsWithoutFolders,
    foldersWithViews,
    folderNotificationCounts,
    totalNotificationCount,
  } = useViewFolderLogic<"returns">({
    allViews,
    allFolders,
    notificationCounts,
  });

  const giftReceiptsEnabled = !!team.settings.returns?.enableGiftReceipts;

  const giftReceiptRequestsCount = useGiftReceiptRequestsCount();
  const giftReceiptRequestsNotificationCount =
    giftReceiptRequestsCount !== undefined && giftReceiptRequestsCount > 0
      ? giftReceiptRequestsCount
      : undefined;

  const { sortableItems, needsAttentionFolderExists } = useMemo(() => {
    void i18n.language;
    const items = viewsAndFoldersToTree({
      viewsWithoutFolders,
      foldersWithViews,
      urlPrefix,
      viewCounts: notificationCounts,
      folderCounts: folderNotificationCounts,
      hideZeroNotifications: false,
    });

    const needsAttentionFolder = items.find(
      (item) =>
        item.type === TreeItemType.Folder &&
        item.name === NEEDS_ATTENTION_FOLDER_NAME,
    );

    if (
      !needsAttentionFolder ||
      needsAttentionFolder.type !== TreeItemType.Folder
    ) {
      return { sortableItems: items, needsAttentionFolderExists: false };
    }

    const extraChildren: TreeViewItem[] = [];

    if (returnsInstantExchangeEnabled) {
      extraChildren.push({
        id: "returns-instant-exchange",
        "data-dd-action-name": "returns_nav:instant_exchange_link_click",
        name: "Instant exchange",
        friendlyName: t("Instant exchange", {
          context:
            "order, fulfillment, claims, and warehouse operations navigation label",
        }),
        type: TreeItemType.View,
        actionHref: routes.getReturnsSubRoute("instant-exchange"),
      });
    }

    if (giftReceiptsEnabled) {
      extraChildren.push({
        id: "gift-receipt-requests",
        "data-dd-action-name": "returns_nav:gift_receipt_requests_link_click",
        name: "Gift receipt requests",
        friendlyName: t("Gift receipt requests", {
          context:
            "order, fulfillment, claims, and warehouse operations navigation label",
        }),
        type: TreeItemType.View,
        actionHref: routes.getReturnsSubRoute("gift-receipt-requests"),
        notificationCount: giftReceiptRequestsNotificationCount,
      });
    }

    needsAttentionFolder.children = [
      ...needsAttentionFolder.children,
      ...extraChildren,
    ];

    return { sortableItems: items, needsAttentionFolderExists: true };
  }, [
    viewsWithoutFolders,
    foldersWithViews,
    urlPrefix,
    notificationCounts,
    folderNotificationCounts,
    returnsInstantExchangeEnabled,
    giftReceiptsEnabled,
    giftReceiptRequestsNotificationCount,
    routes,
    t,
    i18n.language,
  ]);

  const handleRequestEdit = useHandler((id: UniqueIdentifier) => {
    returnViewOrderingContext.openEditFolderModal(id.toString());
  });

  const handleUpdateOrdering = useHandler((items: TreeItem[]) => {
    const flattenedItems = flattenTree(items);
    const { views, folders } = treeToViewsAndFolders<"returns">(
      flattenedItems,
      allViews,
      allFolders,
    );
    returnViewOrderingContext.updateOrdering(views, folders);
  });

  const namedSortableItems = useMemo(
    () => nameReturnsNavTree(sortableItems, "returns"),
    [sortableItems],
  );

  const inmarIntegrationLoad = useLoad(async () => {
    return await rpcClient.getInmarIntegration({});
  }, []);

  const anyChildSelected = useMatch(`${urlPrefix}/*`);

  if (!returnsEnabled || !hasPermissionToViewItem) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: urlPrefix }}
      anyChildSelected={!!anyChildSelected}
      data-dd-action-name="returns_nav:returns_link_click"
      FilledIcon={RETURNS_ICON_FILLED}
      friendlyName={t("Returns", {
        context:
          "order, fulfillment, claims, and warehouse operations navigation label",
      })}
      id="returns"
      notificationCount={totalNotificationCount}
      UnfilledIcon={RETURNS_ICON}
    >
      <AllReturnsItem />
      <GiftReceiptRequestsItem
        hidden={!giftReceiptsEnabled || needsAttentionFolderExists}
        notificationCount={giftReceiptRequestsNotificationCount}
      />
      <InmarBlindReturnsItem
        hidden={!inmarIntegrationLoad.value?.inmarIntegration?.enabled}
      />
      <InstantExchangeItem
        hidden={!returnsInstantExchangeEnabled || needsAttentionFolderExists}
      />
      <SortableTree
        items={namedSortableItems}
        onRequestEdit={handleRequestEdit}
        setItems={handleUpdateOrdering}
      />
      <ReturnsAddViewItem />
      <ReturnsCreateFolderItem />
    </RedoSecondaryNavItem>
  );
});

const AllReturnsItem = memo(function AllReturnsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: recordListHref(routes.returnsBaseUrl, ReturnTypeEnum.RETURN),
      }}
      data-dd-action-name="returns_nav:all_returns_link_click"
      friendlyName={t("All returns", {
        context:
          "order, fulfillment, claims, and warehouse operations navigation label",
      })}
      id="all-returns"
    />
  );
});

const GiftReceiptRequestsItem = memo(function GiftReceiptRequestsItem({
  hidden,
  notificationCount,
}: {
  hidden: boolean;
  notificationCount?: number;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getReturnsSubRoute("gift-receipt-requests"),
      }}
      data-dd-action-name="returns_nav:gift_receipt_requests_link_click"
      friendlyName={t("Gift receipt requests", {
        context:
          "order, fulfillment, claims, and warehouse operations navigation label",
      })}
      hidden={hidden}
      id="gift-receipt-requests"
      notificationCount={notificationCount}
    />
  );
});

const InmarBlindReturnsItem = memo(function InmarBlindReturnsItem({
  hidden,
}: {
  hidden: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getReturnsSubRoute("inmar-blind-returns"),
      }}
      data-dd-action-name="returns_nav:inmar_blind_returns_link_click"
      friendlyName={t("{{providerName}} blind returns", {
        providerName: "Inmar",
        context:
          "logistics navigation label for returns received by the named provider without a matching original order or return authorization, not white-label branding",
      })}
      hidden={hidden}
      id="inmar-blind-returns"
    />
  );
});

const InstantExchangeItem = memo(function InstantExchangeItem({
  hidden,
}: {
  hidden: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getReturnsSubRoute("instant-exchange"),
      }}
      data-dd-action-name="returns_nav:instant_exchange_link_click"
      friendlyName={t("Instant exchange", {
        context:
          "order, fulfillment, claims, and warehouse operations navigation label",
      })}
      hidden={hidden}
      id="returns-instant-exchange"
    />
  );
});

const ReturnsAddViewItem = memo(function ReturnsAddViewItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getReturnsSubRoute("create") }}
      data-dd-action-name="returns_nav:add_return_view_link_click"
      friendlyName={t("+ Add new view", {
        context:
          "order, fulfillment, claims, and warehouse operations navigation label",
      })}
      id="orders-returns-create"
    />
  );
});

const ReturnsCreateFolderItem = memo(function ReturnsCreateFolderItem() {
  const { t } = useTranslation();
  const returnViewOrderingContext = useRequiredContext(
    ReturnViewOrderingContext,
  );

  return (
    <RedoSecondaryNavItem
      action={{
        type: "button",
        onClick: () => {
          returnViewOrderingContext.openCreateFolderModal();
        },
      }}
      data-dd-action-name="returns_nav:add_return_folder_button_click"
      friendlyName={t("+ Add new folder", {
        context:
          "order, fulfillment, claims, and warehouse operations navigation label",
      })}
      id="orders-returns-create-folder"
    />
  );
});
